import { useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';

export interface SystemConfiguration {
    system_name: string;
    system_logo_url?: string;
    system_favicon_url?: string;
    trader_favicon_url?: string;
    trader_app_icon_url?: string;
    logo_url?: string; // Alias for system_logo_url for backward compatibility
    favicon_url?: string; // Alias for system_favicon_url for backward compatibility
    support_email: string;
    support_phone?: string;
    support_address?: string;
    trading_enabled: boolean;
    registration_enabled: boolean;
    maintenance_mode: boolean;
    maintenance_message?: string;
    default_currency: string;
    timezone: string;
    date_format: string;
    time_format: string;
    locale?: string;

    /**
     * Per-tenant branding. Optional: when the backend does not send them the UI
     * keeps the default palette, so this is safe to deploy ahead of the API.
     * Hex strings, e.g. "#2563eb". See src/lib/theme.ts.
     */
    theme_primary_color?: string | null;
    theme_accent_color?: string | null;
    /** Corner radius in px applied to the whole UI. */
    theme_radius?: number | null;
    /** Colour mode new users get before they choose one themselves. */
    theme_default_mode?: 'light' | 'dark' | 'system' | null;
}

export const useSystemConfiguration = () => {
    return useQuery<SystemConfiguration>({
        queryKey: ['system-configuration-public'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<SystemConfiguration>>(
                API_ENDPOINTS.SYSTEM.CONFIGURATION
            );
            return data.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

export const useSystemStatus = () => {
    const queryClient = useQueryClient();

    const query = useQuery<{
        maintenance_mode: boolean;
        maintenance_message?: string;
        trading_enabled: boolean;
        registration_enabled: boolean;
        system_name: string;
        support_email: string;
        support_phone?: string;
    }>({
        queryKey: ['system-status'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<any>>(API_ENDPOINTS.SYSTEM.STATUS);
            return data.data;
        },
        staleTime: 30 * 1000, // 30 seconds - more responsive for critical settings
        gcTime: 1 * 60 * 1000, // 1 minute
        refetchOnWindowFocus: true, // Refetch when window regains focus
        refetchOnMount: true, // Always refetch when component mounts
    });

    const refreshSystemStatus = () => {
        queryClient.invalidateQueries({ queryKey: ['system-status'] });
    };

    return {
        ...query,
        refreshSystemStatus,
    };
};
